import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, inject, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe, DatePipe } from '@angular/common';
import { MatIconButton, MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FreeFilterInputComponent } from '../../../utils/component/free-filter-input/free-filter-input.component';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { TypeCBComponent } from '../../../utils/component/type-filter-combobox/type-cb.component';
import { TranslateComponent } from '../../../core/components/translation/translation.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { FunctionalHelpEntityService } from '../../services/functional-help.entity.service';
import { LocalStorageService } from '../../../core/services/local-storage.service';
import { BehaviorSubject, combineLatest, firstValueFrom, from, map, Observable, switchMap, tap } from 'rxjs';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ConsumerEntityService } from '../../../consumer/services/consumer.entity.service';
import { WidgetMessageService } from '../../../core/services/widget-message.service';
import { SubjectEntityService } from '../../../subject/services/subject.entity.service';
import { FunctionalHelpEntity } from '../../functional-help.entity';
import { ConsumerEntity } from '../../../consumer/consumer.entity';
import { SubjectEntity } from '../../../subject/subject.entity';

import { EntityStateModel, InvalidateLoadedQueries } from '../../../core/state/entity.state';
import { makeQueryKey } from '../../../utils/entity-states/query-key';
import { ExerciseKind, getExerciseKinds } from '../../../core/types';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ConfirmDialogComponent } from '../../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { SimpleConfirmData } from '../../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { EditFunctionalHelpComponent } from '../../edit-functionalHelp/edit-functional-help.component';
import { TableLoadingSpinnerComponent } from '../../../utils/component/table-loading-spinner/table-loading-spinner';
import { BaseFilterComponent } from '../../../utils/component/base-filter-component/base-filter-component';
import { IFilters } from '../../../utils/component/base-filter-component/IFilter';

@Component({
  selector: 'pixl-functional-help',
  imports: [
    MatIcon,
    MatSortModule,
    MatIconButton,
    MatTableModule,
    MatButtonModule,
    MatSelectModule,
    MatTooltipModule,
    MatDialogModule,
    MatListModule,
    MatInputModule,
    DatePipe,
    FreeFilterInputComponent,
    TypeCBComponent,
    TranslateComponent,
    TranslocoModule,
    MatPaginator,
    AsyncPipe,
    TableLoadingSpinnerComponent,
    FreeFilterInputComponent,
  ],
  templateUrl: './functional-help.component.html',
  styleUrl: './functional-help.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunctionalHelpComponent extends BaseFilterComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('editStateDiv') editStateRef!: ElementRef;

  totalItems = 1000;

  private readonly store = inject(Store);
  private functionHelpService = inject(FunctionalHelpEntityService);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private widgetService = inject(WidgetMessageService);
  functionalHelps$!: Observable<FunctionalHelpEntity[]>;
  private dialog = inject(MatDialog);
  deletedIds$ = new BehaviorSubject<string[]>([]);
  private refresh$ = new BehaviorSubject<void>(undefined);
  private readonly pageChange$ = toObservable(this.pageChange);

  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;

  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  public filters: IFilters = {};
  private sortString = 'name';

  private _liveAnnouncer = inject(LiveAnnouncer);
  private translocoService = inject(TranslocoService);
  private localStorageService = inject(LocalStorageService);

  public dataSource = new MatTableDataSource<FunctionalHelpEntity>([] as FunctionalHelpEntity[]);
  public displayedColumns: string[] = ['edit-actions', 'name', 'consumer', 'exerciseKind', 'competency', 'created'];
  public kinds: ExerciseKind[] = [];

  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;

  ngOnInit() {
    this.kinds = getExerciseKinds();
    this.loadRelationData();

    this.functionalHelps$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query: Record<string, string | number> = {
          'include': 'consumers,subjects',
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'sort': this.sortString,
        };

        if (this.freeFilter?.trim()?.length >= this.freeFilterLen) {
          query['filter[idOrNameLike]'] = this.freeFilter.trim();
        }

        if (this.consumerFilter?.length > 0) {
          query['filter[consumers][id]'] = this.consumerFilter.join(',');
        }

        if (this.subjectFilter?.length > 0) {
          query['filter[subjectId]'] = this.subjectFilter.join(',');
        }

        if (this.exerciseKindFilter?.length > 0) {
          query['filter[kind]'] = this.exerciseKindFilter.trim();
        }

        return from(this.functionHelpService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.functionHelpService.allByQuery(query))),
          map((entities: FunctionalHelpEntity[]) => entities.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            const state = this.store.selectSnapshot(this.functionHelpService.stateClass) as EntityStateModel<FunctionalHelpEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state.meta?.[queryKey]?.page?.total ?? 0;
            this.cdr.markForCheck();
          })
        );
      })
    );
  }

  ngAfterViewInit(): void {
    this.paginator().page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: PageEvent) => {
      this.pageChange.set({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  private loadRelationData(): void {
    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(this.consumerService.ensureLoaded(consumerQuery)).pipe(
      switchMap(() => this.store.select(this.consumerService.allByQuery(consumerQuery)))
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(this.subjectService.ensureLoaded(subjectQuery)).pipe(
      switchMap(() => this.store.select(this.subjectService.allByQuery(subjectQuery)))
    );
  }

  addFunctionalHelp(): void {
    const dialogRef = this.dialog.open(EditFunctionalHelpComponent, {
      width: '500px'
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<FunctionalHelpEntity> | undefined) => {
      if (result) {
        const newEntityInstance = Object.assign(new FunctionalHelpEntity(), result);
        await this.functionHelpService.create(newEntityInstance);
        this.store.dispatch(new InvalidateLoadedQueries());
        this.refresh$.next();

        this.widgetService.setMessage({
          message: this.translocoService.translate('task_created'),
          messageTye: 'mtSuccess'
        });
      }
    });
  }

  public async copyFunctionalHelp(functionalHelp: FunctionalHelpEntity) {
    const copyEntityInstance = Object.assign(new FunctionalHelpEntity(), functionalHelp);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (copyEntityInstance as any).id = undefined;
    copyEntityInstance.name = `${copyEntityInstance.name} (Kopie)`;
    copyEntityInstance.version = 1;
    await this.functionHelpService.create(copyEntityInstance);
    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: this.translocoService.translate('task_cloned'),
      messageTye: 'mtSuccess'
    });
  }

  public async deleteFunctionalHelp(functionalHelp: FunctionalHelpEntity): Promise<void> {
    if (!functionalHelp?.id) return;

    const confirmed = await firstValueFrom(
      this.dialog.open<ConfirmDialogComponent, SimpleConfirmData, boolean>(ConfirmDialogComponent, {
        data: {
          name: String(functionalHelp.name),
          headerText: this.translocoService.translate('task_delete'),
        }
      }).afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(this.functionHelpService.delete(functionalHelp.id));
    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: this.translocoService.translate('task_deleted'),
      messageTye: 'mtSuccess'
    });
  }

  editFunctionalHelp(functionalHelp: FunctionalHelpEntity): void {
    const dialogRef = this.dialog.open(EditFunctionalHelpComponent, {
      width: '800px',
      data: {
        ...functionalHelp,
        consumerExtra: this.getConsumer(functionalHelp.consumerId)
      },
    });

    dialogRef.afterClosed().subscribe(async (updated: FunctionalHelpEntity | undefined) => {
      if (!updated) return;
      await firstValueFrom(this.functionHelpService.update(updated));
      this.refresh$.next();

      this.widgetService.setMessage({
        message: this.translocoService.translate('task_saved'),
        messageTye: 'mtSuccess'
      });
    });
  }

  public announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    }
    else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
    this.sortData(sortState);
  }

  public sortData(sort: Sort): void {
    this.sortString = sort.direction === 'desc' ? `-${sort.active}` : sort.active;
    this.refresh$.next();
  }

  public getCreatedDate(id: string): Date {
    return new Date(parseInt(id.substring(0, 8), 16) * 1000);
  }

  getConsumer(consumerId: string, getName = false): ConsumerEntity | string {
    if (!consumerId) return '---';
    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    const consumer = this.consumerService.stateClass.byId(state)(consumerId);
    return getName ? (consumer?.name || consumerId) : consumer;
  }

  getSubject(subjectId: string, getName = false): SubjectEntity | string {
    if (!subjectId) return '---';
    const state = this.store.selectSnapshot(this.subjectService.stateClass);
    const subject = this.subjectService.stateClass.byId(state)(subjectId);
    return getName ? (subject?.name || subjectId) : subject;
  }

  public changeFunctionalHelpState(functionalHelp: FunctionalHelpEntity) {
    if (functionalHelp.approval === 'Dormant') {
      return;
    }

    if (this.editStateRef.nativeElement.classList.contains('open')) {
      this.editStateRef.nativeElement.classList.remove('open');
    }
    else {
      this.editStateRef.nativeElement.classList.add('open');
    }
  }
}
